import ErrorBoundary from "@components/layout/ErrorBoundary";
import Layout from "@components/layout/Layout";
import MinScreenSizeWarning from "@components/layout/MinScreenSizeWarning";
import ModalProvider from "@components/providers/ModalProvider";
import ActionPermissionsContextProvider from "@contexts/ActionPermissionsContext";
import OrgSharpConfigContextProvider from "@contexts/OrgSharpConfigContext/OrgSharpConfigContext";
import TagContextProvider from "@contexts/TagContext/TagContext";
import { TrackingContextProvider } from "@contexts/TrackingContext";
import TranslationContextProvider from "@contexts/TranslationCacheContext";
import UserContext, { useUserContextState } from "@contexts/UserContext";
import { deprecatedTones } from "@src/deprecatedDesignSystem/styles/deprecatedColors";
import { useFullStory } from "@hooks/useFullStory";
import useUpdateIntercom from "@hooks/useUpdateIntercom";
import * as RadixTooltip from "@radix-ui/react-tooltip";
import { css, StyleSheet } from "aphrodite";
import Fuse from "fuse.js";
import Head from "next/head";
import { FC, ReactNode } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { SkeletonTheme } from "react-loading-skeleton";
import { usePurgeCacheOnUserChange } from "@src/hooks/usePurgeCacheOnUserChange";

type Props = {
  children: ReactNode;
};

Fuse.config.ignoreLocation = true;

export const AuthedApp: FC<Props> = (props: Props) => {
  const userContext = useUserContextState();
  useUpdateIntercom(userContext);
  useFullStory();
  usePurgeCacheOnUserChange();
  if (!userContext.user) {
    return null;
  }

  return (
    <ErrorBoundary>
      <RadixTooltip.Provider skipDelayDuration={0}>
        <TranslationContextProvider>
          <ModalProvider>
            <UserContext.Provider value={userContext}>
              <ActionPermissionsContextProvider>
                <TrackingContextProvider userContext={userContext}>
                  <SkeletonTheme
                    baseColor={deprecatedTones.gray5Alpha}
                    highlightColor={deprecatedTones.gray4Alpha}
                  >
                    <DndProvider backend={HTML5Backend}>
                      <TagContextProvider>
                        <OrgSharpConfigContextProvider>
                          <div className={css(styles.container)}>
                            <Head>
                              <title>
                                Opus | {userContext.user?.org!.name}
                              </title>
                              <link
                                rel="icon"
                                href={
                                  window.location.href.includes("://localhost:")
                                    ? "/favicon-lcl.ico"
                                    : window.location.href.includes(".rocks")
                                      ? "/favicon-dev.ico"
                                      : "/favicon.ico"
                                }
                              ></link>
                            </Head>
                            <Layout>
                              <MinScreenSizeWarning>
                                {props.children}
                              </MinScreenSizeWarning>
                            </Layout>
                          </div>
                        </OrgSharpConfigContextProvider>
                      </TagContextProvider>
                    </DndProvider>
                  </SkeletonTheme>
                </TrackingContextProvider>
              </ActionPermissionsContextProvider>
            </UserContext.Provider>
          </ModalProvider>
        </TranslationContextProvider>
      </RadixTooltip.Provider>
    </ErrorBoundary>
  );
};

const styles = StyleSheet.create({
  container: {
    display: "flex",
    top: 0,
    bottom: 0,
    right: 0,
    left: 0,
    minHeight: "100vh",
  },
});

export default AuthedApp;
